import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const AttorneyProfile = ({ attorney, currentLanguage }) => {
  const [activeTab, setActiveTab] = useState('biography');

  const tabs = [
    { id: 'biography', label: currentLanguage === 'en' ? 'Biography' : 'Wasifu' },
    { id: 'expertise', label: currentLanguage === 'en' ? 'Expertise' : 'Utaalamu' },
    { id: 'achievements', label: currentLanguage === 'en' ? 'Achievements' : 'Mafanikio' },
    { id: 'publications', label: currentLanguage === 'en' ? 'Publications' : 'Machapisho' }
  ];

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(currentLanguage === 'en' 
      ? `Hello ${attorney.name}, I would like to schedule a consultation regarding legal services.`
      : `Hujambo ${attorney.name}, ningependa kupanga mazungumzo kuhusu huduma za kisheria.`);
    window.open(`https://wa.me/${attorney.phone}?text=${message}`, '_blank');
  };

  const handleCallClick = () => {
    window.open(`tel:${attorney.phone}`, '_self');
  };

  return (
    <div className="bg-white rounded-lg shadow-lg border border-border-light overflow-hidden">
      {/* Profile Header */}
      <div className="p-6 lg:p-8 bg-gradient-to-r from-surface/20 to-primary/10">
        <div className="flex flex-col lg:flex-row items-center lg:items-start space-y-6 lg:space-y-0 lg:space-x-8">
          {/* Profile Image */}
          <div className="flex-shrink-0">
            <div className="w-32 h-32 lg:w-40 lg:h-40 rounded-full overflow-hidden border-4 border-white shadow-lg">
              <Image
                src={attorney.image}
                alt={attorney.name}
                className="w-full h-full object-cover"
              />
            </div>
          </div>

          {/* Profile Info */}
          <div className="flex-1 text-center lg:text-left">
            <h2 className="text-2xl lg:text-3xl font-heading font-bold text-text-primary mb-2">
              {attorney.name}
            </h2>
            <p className="text-lg text-primary font-semibold mb-3">
              {attorney.title}
            </p>
            <p className="text-text-secondary mb-4 leading-relaxed">
              {attorney.shortBio}
            </p>

            {/* Contact Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start">
              <Button
                variant="primary"
                onClick={handleWhatsAppClick}
                iconName="MessageCircle"
                iconPosition="left"
                size="sm"
              >
                WhatsApp
              </Button>
              <Button
                variant="outline"
                onClick={handleCallClick}
                iconName="Phone"
                iconPosition="left"
                size="sm"
              >
                {currentLanguage === 'en' ? 'Call' : 'Piga simu'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div className="border-b border-border-light">
        <div className="flex overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex-shrink-0 px-6 py-4 text-sm font-medium transition-colors duration-200 border-b-2 ${
                activeTab === tab.id
                  ? 'text-primary border-primary bg-surface/10' :'text-text-secondary border-transparent hover:text-primary hover:border-primary/30'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div className="p-6 lg:p-8">
        {activeTab === 'biography' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3">
                {currentLanguage === 'en' ? 'Professional Background' : 'Mazingira ya Kitaalamu'}
              </h3>
              <p className="text-text-secondary leading-relaxed">
                {attorney.biography}
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3">
                {currentLanguage === 'en' ? 'Education' : 'Elimu'}
              </h3>
              <ul className="space-y-2">
                {attorney.education.map((edu, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="GraduationCap" size={16} className="text-primary mt-1 flex-shrink-0" />
                    <span className="text-text-secondary">{edu}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3">
                {currentLanguage === 'en' ? 'Bar Admissions' : 'Uthibitisho wa Mahakama'}
              </h3>
              <ul className="space-y-2">
                {attorney.barAdmissions.map((admission, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="Scale" size={16} className="text-primary mt-1 flex-shrink-0" />
                    <span className="text-text-secondary">{admission}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'expertise' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                {currentLanguage === 'en' ? 'Practice Areas' : 'Maeneo ya Utendaji'}
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {attorney.practiceAreas.map((area, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-surface/10 rounded-lg">
                    <Icon name="CheckCircle" size={16} className="text-primary mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-text-primary">{area.name}</h4>
                      <p className="text-sm text-text-secondary mt-1">{area.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3">
                {currentLanguage === 'en' ? 'Professional Memberships' : 'Uanachama wa Kitaalamu'}
              </h3>
              <ul className="space-y-2">
                {attorney.memberships.map((membership, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="Users" size={16} className="text-primary mt-1 flex-shrink-0" />
                    <span className="text-text-secondary">{membership}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}

        {activeTab === 'achievements' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                {currentLanguage === 'en' ? 'Notable Achievements' : 'Mafanikio Makubwa'}
              </h3>
              <div className="space-y-4">
                {attorney.achievements.map((achievement, index) => (
                  <div key={index} className="flex items-start space-x-3 p-4 bg-success/5 border-l-4 border-success rounded-r-lg">
                    <Icon name="Award" size={16} className="text-success mt-1 flex-shrink-0" />
                    <div>
                      <h4 className="font-medium text-text-primary">{achievement.title}</h4>
                      <p className="text-sm text-text-secondary mt-1">{achievement.description}</p>
                      <span className="text-xs text-success font-medium">{achievement.year}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3">
                {currentLanguage === 'en' ? 'Client Testimonials' : 'Ushuhuda wa Wateja'}
              </h3>
              <div className="space-y-4">
                {attorney.testimonials.map((testimonial, index) => (
                  <div key={index} className="p-4 bg-surface/10 rounded-lg">
                    <p className="text-text-secondary italic mb-2">"{testimonial.content}"</p>
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium text-text-primary">{testimonial.client}</span>
                      <span className="text-xs text-text-secondary">- {testimonial.case}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'publications' && (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-4">
                {currentLanguage === 'en' ? 'Articles & Publications' : 'Makala na Machapisho'}
              </h3>
              <div className="space-y-4">
                {attorney.publications.map((publication, index) => (
                  <div key={index} className="p-4 border border-border-light rounded-lg hover:shadow-md transition-shadow duration-200">
                    <h4 className="font-medium text-text-primary mb-2">{publication.title}</h4>
                    <p className="text-sm text-text-secondary mb-2">{publication.description}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-xs text-primary font-medium">{publication.publication}</span>
                      <span className="text-xs text-text-secondary">{publication.date}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-text-primary mb-3">
                {currentLanguage === 'en' ? 'Speaking Engagements' : 'Mazungumzo ya Umma'}
              </h3>
              <ul className="space-y-3">
                {attorney.speakingEngagements.map((engagement, index) => (
                  <li key={index} className="flex items-start space-x-3">
                    <Icon name="Mic" size={16} className="text-primary mt-1 flex-shrink-0" />
                    <div>
                      <span className="text-text-secondary">{engagement.event}</span>
                      <span className="text-xs text-text-secondary block">{engagement.date}</span>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AttorneyProfile;